const DEFAULT_CSRF_COOKIE = 'csrf';

class HttpError extends Error {
  constructor(message, response, body) {
    super(message);
    this.name = 'HttpError';
    this.response = response;
    this.status = response?.status ?? null;
    this.body = body;
  }
}

const readCookieValue = (name) => {
  if (!name || typeof document === 'undefined') {
    return undefined;
  }
  const source = typeof document.cookie === 'string' ? document.cookie : '';
  if (!source) {
    return undefined;
  }
  const prefix = `${name}=`;
  const match = source
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!match) {
    return undefined;
  }
  const value = match.slice(prefix.length);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normaliseMethod = (method) => {
  if (!method) {
    return 'GET';
  }
  return String(method).toUpperCase();
};

const buildHeaders = ({ baseHeaders = {}, csrfToken }) => {
  if (!csrfToken) {
    return baseHeaders;
  }
  return { ...baseHeaders, 'X-CSRF-Token': csrfToken };
};

const safeParseJson = async (response) => {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new HttpError('Failed to parse JSON response', response, text);
  }
};

const requestJson = async (
  base,
  path,
  {
    method,
    headers = {},
    body,
    csrfToken,
    csrfCookieName = DEFAULT_CSRF_COOKIE,
    credentials = 'include',
    ...rest
  } = {}
) => {
  if (!base) {
    throw new Error('Base URL is required');
  }
  const resolvedMethod = normaliseMethod(method);
  const shouldAttachCsrf = resolvedMethod !== 'GET' && resolvedMethod !== 'HEAD';
  const finalCsrfToken =
    csrfToken || (shouldAttachCsrf ? readCookieValue(csrfCookieName) : undefined);
  const response = await fetch(`${base}${path}`, {
    ...rest,
    method: resolvedMethod,
    headers: buildHeaders({ baseHeaders: headers, csrfToken: finalCsrfToken }),
    credentials,
    body,
  });
  const data = await safeParseJson(response);
  if (!response.ok) {
    const message =
      (data && typeof data === 'object' && data.error) ||
      `Request failed with status ${response.status}`;
    throw new HttpError(message, response, data);
  }
  return data;
};

export { HttpError, readCookieValue as readCsrf, requestJson };
